import React, { useState } from 'react';
import { Box, Typography, Grid, Paper, Avatar, Chip, Button, Slider, TextField } from '@mui/material';
import { styled } from '@mui/material/styles';
import { Award, Star, Database, RefreshCw, User, Bot } from 'lucide-react';

interface AttributionPanelProps {
  proposalId: string | null;
}

const SectionContainer = styled(Box)(({ theme }) => ({
  background: 'rgba(26, 26, 46, 0.6)',
  backdropFilter: 'blur(10px)',
  borderRadius: theme.spacing(2),
  border: '1px solid rgba(138, 43, 226, 0.2)',
  padding: theme.spacing(3),
  marginBottom: theme.spacing(4),
  boxShadow: '0 4px 30px rgba(0, 0, 0, 0.1)',
}));

const ContributorCard = styled(Paper)(({ theme }) => ({
  background: 'rgba(26, 26, 46, 0.7)',
  backdropFilter: 'blur(10px)',
  borderRadius: theme.spacing(2),
  border: '1px solid rgba(138, 43, 226, 0.2)',
  padding: theme.spacing(2),
  height: '100%',
  transition: 'all 0.3s ease',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: '0 10px 30px rgba(138, 43, 226, 0.3)',
    border: '1px solid rgba(138, 43, 226, 0.5)',
  },
}));

const AttributionChip = styled(Chip)(({ theme }) => ({
  margin: theme.spacing(0.5),
  background: 'rgba(138, 43, 226, 0.2)',
  color: '#fff',
  borderRadius: '4px',
}));

const RoyaltySlider = styled(Slider)(({ theme }) => ({
  color: '#8A2BE2',
  '& .MuiSlider-thumb': {
    width: 16,
    height: 16,
    backgroundColor: '#00CC99',
  },
  '& .MuiSlider-rail': {
    opacity: 0.5,
    backgroundColor: 'rgba(138, 43, 226, 0.2)',
  },
  '& .MuiSlider-track': {
    background: 'linear-gradient(45deg, #8A2BE2 30%, #00CC99 90%)',
  },
}));

// Mock contributors data
const contributors = [
  {
    id: '1',
    name: 'Jane Smith',
    type: 'human',
    role: 'Problem Creator',
    avatar: '/images/leaf.png',
    contributions: ['Problem Statement', 'Requirements', 'User Journey'],
    expertise: ['Customer Service', 'Process Optimization'],
    attribution: 35,
    royaltyRate: 3.5
  },
  {
    id: '2',
    name: 'AI Assistant',
    type: 'ai',
    role: 'Solution Provider',
    avatar: '/images/bot.png',
    contributions: ['Technical Solution', 'Implementation Plan', 'Scale Metrics'],
    expertise: ['Machine Learning', 'Natural Language Processing', 'System Design'],
    attribution: 65,
    royaltyRate: 2.0
  }
];

const AttributionPanel: React.FC<AttributionPanelProps> = ({ proposalId }) => {
  const [attributionData, setAttributionData] = useState(contributors);
  
  const handleRoyaltyChange = (id: string, newValue: number) => {
    setAttributionData(attributionData.map(contributor => 
      contributor.id === id ? { ...contributor, royaltyRate: newValue } : contributor
    ));
  };

  const handleAttributionChange = (id: string, newValue: number) => {
    // Update attribution for this contributor
    let updatedContributors = attributionData.map(contributor => 
      contributor.id === id ? { ...contributor, attribution: newValue } : contributor
    );
    
    // Calculate total attribution
    const total = updatedContributors.reduce((sum, contributor) => sum + contributor.attribution, 0);
    
    // If total is not 100%, adjust other contributors proportionally
    if (total !== 100) {
      const diff = 100 - total;
      const otherContributors = updatedContributors.filter(c => c.id !== id);
      
      if (otherContributors.length > 0) {
        // Calculate the sum of other contributors' attribution
        const otherSum = otherContributors.reduce((sum, c) => sum + c.attribution, 0);
        
        // Adjust each other contributor proportionally
        updatedContributors = updatedContributors.map(c => {
          if (c.id !== id && otherSum > 0) {
            const adjustmentRatio = c.attribution / otherSum;
            return { ...c, attribution: Math.max(5, c.attribution + (diff * adjustmentRatio)) };
          }
          return c;
        });
      }
    }
    
    setAttributionData(updatedContributors);
  };

  const handleResetAttribution = () => {
    setAttributionData(contributors);
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <Award size={24} color="#FFD700" style={{ marginRight: 12 }} />
        <Typography variant="h6" sx={{ color: '#fff' }}>
          Attribution & Recognition
        </Typography>
      </Box>
      
      <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)', mb: 3 }}>
        Acknowledge and reward the contributors to this solution. Attribution affects visibility on leaderboards and recognition in the ecosystem.
      </Typography>
      
      <SectionContainer>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="subtitle1" sx={{ color: '#FFD700' }}>
            Contributors
          </Typography>
          
          <Button 
            startIcon={<RefreshCw size={16} />} 
            onClick={handleResetAttribution}
            sx={{ color: 'rgba(255, 255, 255, 0.7)' }}
          >
            Reset
          </Button>
        </Box>
        
        <Grid container spacing={3}>
          {attributionData.map((contributor) => (
            <Grid item xs={12} md={6} key={contributor.id}>
              <ContributorCard>
                <Box sx={{ display: 'flex', mb: 2 }}>
                  <Avatar 
                    src={contributor.avatar} 
                    sx={{ 
                      width: 50, 
                      height: 50, 
                      mr: 2,
                      border: contributor.type === 'human' 
                        ? '2px solid #00CC99' 
                        : '2px solid #8A2BE2'
                    }} 
                  />
                  <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Typography variant="subtitle1" sx={{ color: '#fff' }}>
                        {contributor.name}
                      </Typography>
                      {contributor.type === 'human' ? (
                        <User size={16} color="#00CC99" style={{ marginLeft: 8 }} />
                      ) : (
                        <Bot size={16} color="#8A2BE2" style={{ marginLeft: 8 }} />
                      )}
                    </Box>
                    <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                      {contributor.role}
                    </Typography>
                  </Box>
                </Box>
                
                <Typography variant="subtitle2" sx={{ color: '#fff', mb: 1 }}>
                  Contributions:
                </Typography>
                <Box sx={{ mb: 2 }}>
                  {contributor.contributions.map((contribution, index) => (
                    <AttributionChip key={index} label={contribution} />
                  ))}
                </Box>
                
                <Typography variant="subtitle2" sx={{ color: '#fff', mb: 1 }}>
                  Expertise:
                </Typography>
                <Box sx={{ mb: 3 }}>
                  {contributor.expertise.map((expertise, index) => (
                    <AttributionChip 
                      key={index} 
                      label={expertise} 
                      icon={<Star size={14} color="#FFD700" />} 
                      sx={{ background: 'rgba(255, 215, 0, 0.2)' }}
                    />
                  ))}
                </Box>
                
                <Typography variant="subtitle2" sx={{ color: '#fff', mb: 1, display: 'flex', alignItems: 'center' }}>
                  <Database size={16} style={{ marginRight: 8 }} />
                  Attribution Percentage:
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                  <RoyaltySlider 
                    value={contributor.attribution}
                    onChange={(_, newValue) => handleAttributionChange(contributor.id, newValue as number)}
                    step={5}
                    min={5}
                    max={95}
                    valueLabelDisplay="auto"
                    sx={{ flex: 1, mr: 2 }}
                  />
                  <Typography variant="subtitle2" sx={{ color: '#00CC99', width: 50, textAlign: 'right' }}>
                    {contributor.attribution}%
                  </Typography>
                </Box>
                
                <Typography variant="subtitle2" sx={{ color: '#fff', mb: 1, display: 'flex', alignItems: 'center' }}>
                  <Star size={16} color="#FFD700" style={{ marginRight: 8 }} />
                  Future Royalty Rate:
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <RoyaltySlider 
                    value={contributor.royaltyRate}
                    onChange={(_, newValue) => handleRoyaltyChange(contributor.id, newValue as number)}
                    step={0.5}
                    min={0}
                    max={10}
                    valueLabelDisplay="auto"
                    sx={{ flex: 1, mr: 2 }}
                  />
                  <Typography variant="subtitle2" sx={{ color: '#FFD700', width: 50, textAlign: 'right' }}>
                    {contributor.royaltyRate}%
                  </Typography>
                </Box>
              </ContributorCard>
            </Grid>
          ))}
        </Grid>
      </SectionContainer>
      
      <SectionContainer>
        <Typography variant="subtitle1" sx={{ color: '#FFD700', mb: 3 }}>
          Recognition Settings
        </Typography>
        
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" sx={{ color: '#fff', mb: 1 }}>
            Leaderboard Visibility:
          </Typography>
          <TextField
            select
            fullWidth
            value="public"
            SelectProps={{
              native: true,
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                color: '#fff',
                '& fieldset': {
                  borderColor: 'rgba(138, 43, 226, 0.3)',
                },
                '&:hover fieldset': {
                  borderColor: 'rgba(138, 43, 226, 0.5)',
                },
                '&.Mui-focused fieldset': {
                  borderColor: '#8a2be2',
                },
              },
            }}
          >
            <option value="public">Public (Visible to all users)</option>
            <option value="private">Private (Only visible to contributors)</option>
            <option value="anonymous">Anonymous (Contribution type only)</option>
          </TextField>
        </Box>
        
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" sx={{ color: '#fff', mb: 1 }}>
            Attribution Notes (Optional):
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={3}
            placeholder="Add any specific notes about the attribution or recognition..."
            sx={{
              textarea: { color: '#fff' },
              '& .MuiOutlinedInput-root': {
                '& fieldset': {
                  borderColor: 'rgba(138, 43, 226, 0.3)',
                },
                '&:hover fieldset': {
                  borderColor: 'rgba(138, 43, 226, 0.5)',
                },
                '&.Mui-focused fieldset': {
                  borderColor: '#8a2be2',
                },
              },
            }}
          />
        </Box>
        
        <Button 
          variant="contained"
          startIcon={<Award size={18} />}
          sx={{
            background: 'linear-gradient(45deg, #FFD700 30%, #FFA500 90%)',
            color: '#000',
            fontWeight: 600,
            '&:hover': {
              background: 'linear-gradient(45deg, #FFA500 30%, #FFD700 90%)',
            },
          }}
        >
          Save Attribution
        </Button>
      </SectionContainer>
    </Box>
  );
};

export default AttributionPanel; 